import gsap from 'gsap';
import KeenSlider from 'keen-slider';
import { query, queryAll } from '../core/dom.js';

const HERO_AUTOPLAY_IMAGE_DELAY_MS = 5000;
const HERO_AUTOPLAY_VIDEO_END_BUFFER_MS = 250;
const HERO_AUTOPLAY_VIDEO_FALLBACK_DURATION_MS = 10 * 60 * 1000;
const HERO_YOUTUBE_API_SRC = 'https://www.youtube.com/iframe_api';
const HERO_TITLE_OFFSET_Y = 12;
const HERO_SUBTITLE_OFFSET_Y = 4;
const HERO_CTA_OFFSET_Y = 0;

const heroVideoPlayers = new WeakMap();
let youtubeIframeApiPromise = null;

function getHeroActiveIndex(ctx, fallbackIndex = 0) {
  return ctx.heroSliderInstance?.track?.details?.rel ?? fallbackIndex;
}

function isHeroSlideActive(ctx, activeIndex) {
  return getHeroActiveIndex(ctx, activeIndex) === activeIndex;
}

function loadYouTubeIframeApi() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('YouTube IFrame API requires window.'));
  }

  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }

  if (youtubeIframeApiPromise) {
    return youtubeIframeApiPromise;
  }

  youtubeIframeApiPromise = new Promise((resolve, reject) => {
    const previousCallback = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      try {
        if (typeof previousCallback === 'function') {
          previousCallback();
        }
      } finally {
        resolve(window.YT);
      }
    };

    const existingScript = document.querySelector('script[src*="youtube.com/iframe_api"]');
    if (existingScript) {
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = HERO_YOUTUBE_API_SRC;
    script.async = true;
    script.addEventListener('error', reject, { once: true });
    document.head.appendChild(script);
  });

  return youtubeIframeApiPromise;
}

function resetSlideContentState(slide) {
  if (!slide) {
    return;
  }

  const titleLines = queryAll('.hero__title-line', slide);
  const divider = query('.hero__divider', slide);
  const subtitle = query('.hero__subtitle', slide);
  const productCards = queryAll('.hero__product-card', slide);

  gsap.killTweensOf([titleLines, divider, subtitle, productCards].flat().filter(Boolean));

  if (titleLines.length) {
    gsap.set(titleLines, { y: 0, opacity: 1 });
  }

  if (divider) {
    gsap.set(divider, { scaleY: 1, opacity: 1, transformOrigin: 'top center' });
  }

  if (subtitle) {
    gsap.set(subtitle, { y: 0, opacity: 1 });
  }

  if (productCards.length) {
    gsap.set(productCards, { y: 0, opacity: 1 });
  }
}

function animateSlideContent(slide) {
  if (!slide) {
    return;
  }

  const titleLines = queryAll('.hero__title-line', slide);
  const divider = query('.hero__divider', slide);
  const subtitle = query('.hero__subtitle', slide);
  const productCards = queryAll('.hero__product-card', slide);

  const timeline = gsap.timeline({ defaults: { duration: 0.6, ease: 'power3.out' } });

  if (titleLines.length) {
    timeline.from(titleLines, {
      y: HERO_TITLE_OFFSET_Y,
      opacity: 0,
      stagger: 0.08,
    });
  }

  if (divider) {
    timeline.from(divider, { scaleY: 0, opacity: 0, transformOrigin: 'top center' }, '<');
  }

  if (subtitle) {
    timeline.from(subtitle, { y: HERO_SUBTITLE_OFFSET_Y, opacity: 0 }, '<');
  }

  if (productCards.length) {
    timeline.from(
      productCards,
      {
        y: HERO_CTA_OFFSET_Y,
        opacity: 0,
        stagger: 0.08,
      },
      '-=0.25',
    );
  }
}

function clearHeroAutoplay(state) {
  if (state.autoplayTimer) {
    window.clearTimeout(state.autoplayTimer);
    state.autoplayTimer = null;
  }

  if (state.videoDurationTimer) {
    window.clearTimeout(state.videoDurationTimer);
    state.videoDurationTimer = null;
  }

  state.videoPlayToken += 1;
}

function triggerHeroNext(ctx, state) {
  clearHeroAutoplay(state);
  ctx.heroSliderInstance?.next();
}

function setSlideState(slides, activeIndex) {
  slides.forEach((slide, index) => {
    const isActive = index === activeIndex;
    slide.classList.toggle('is-active', isActive);
    slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
  });
}

function goToHeroSlide(ctx, slides, targetIndex) {
  if (!slides.length) {
    return;
  }

  const normalizedIndex = ((targetIndex % slides.length) + slides.length) % slides.length;
  ctx.heroSliderInstance?.moveToIdx(normalizedIndex, true);
}

function advanceHeroAutoplay(ctx, slides, activeIndex, state) {
  clearHeroAutoplay(state);
  goToHeroSlide(ctx, slides, activeIndex + 1);
}

function scheduleVideoFallback(ctx, slides, activeIndex, iframe, state) {
  const videoDurationMs = Number.parseInt(iframe.getAttribute('data-hero-video-duration-ms') ?? '0', 10);
  const scheduledVideoDurationMs = Number.isFinite(videoDurationMs) && videoDurationMs > 0
    ? videoDurationMs
    : HERO_AUTOPLAY_VIDEO_FALLBACK_DURATION_MS;

  state.videoDurationTimer = window.setTimeout(() => {
    if (isHeroSlideActive(ctx, activeIndex)) {
      advanceHeroAutoplay(ctx, slides, activeIndex, state);
    }
  }, scheduledVideoDurationMs + HERO_AUTOPLAY_VIDEO_END_BUFFER_MS);
}

function ensureHeroVideoSrc(iframe) {
  const videoSrc = iframe.getAttribute('data-hero-video-src') ?? '';

  if (videoSrc && !iframe.getAttribute('src')) {
    iframe.setAttribute('src', videoSrc);
  }

  return videoSrc;
}

function stopInactiveHeroVideo(iframe) {
  const record = heroVideoPlayers.get(iframe);

  if (record?.player) {
    try {
      record.player.pauseVideo?.();
      record.player.stopVideo?.();
    } catch (_error) {
      // YouTube can throw while an iframe is still booting; the next activation recreates playback state.
    }
    return;
  }

  if (iframe.hasAttribute('src')) {
    iframe.removeAttribute('src');
  }
}

function getOrCreateHeroVideoPlayer(iframe, slideIndex, ctx, slides, state) {
  const existingRecord = heroVideoPlayers.get(iframe);
  if (existingRecord) {
    return existingRecord.readyPromise;
  }

  const readyPromise = loadYouTubeIframeApi().then((YT) => new Promise((resolve, reject) => {
    if (!YT?.Player || !document.body.contains(iframe) || !isHeroSlideActive(ctx, slideIndex)) {
      reject(new Error('YouTube player is not available.'));
      return;
    }

    const record = {
      player: null,
      readyPromise: null,
    };

    const player = new YT.Player(iframe, {
      events: {
        onReady: () => {
          resolve(player);
        },
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.ENDED && isHeroSlideActive(ctx, slideIndex) && slides.length > 1) {
            triggerHeroNext(ctx, state);
          }
        },
      },
    });

    record.player = player;
    record.readyPromise = readyPromise;
    heroVideoPlayers.set(iframe, record);
  }));

  readyPromise.catch(() => {
    if (heroVideoPlayers.get(iframe)?.readyPromise === readyPromise) {
      heroVideoPlayers.delete(iframe);
    }
  });

  heroVideoPlayers.set(iframe, {
    player: null,
    readyPromise,
  });

  return readyPromise;
}

function playActiveHeroVideo(iframe, activeIndex, ctx, slides, state) {
  const videoSrc = ensureHeroVideoSrc(iframe);

  if (!videoSrc) {
    return;
  }

  const playToken = state.videoPlayToken;

  getOrCreateHeroVideoPlayer(iframe, activeIndex, ctx, slides, state)
    .then((player) => {
      if (state.videoPlayToken !== playToken || !isHeroSlideActive(ctx, activeIndex)) {
        return;
      }

      try {
        player.mute?.();
        player.seekTo?.(0, true);
        player.playVideo?.();
      } catch (_error) {
        if (!state.videoDurationTimer) {
          scheduleVideoFallback(ctx, slides, activeIndex, iframe, state);
        }
      }
    })
    .catch(() => {
      if (state.videoPlayToken === playToken && isHeroSlideActive(ctx, activeIndex) && !state.videoDurationTimer) {
        scheduleVideoFallback(ctx, slides, activeIndex, iframe, state);
      }
    });
}

function scheduleHeroAutoplay(ctx, slides, activeIndex, state) {
  clearHeroAutoplay(state);

  if (slides.length <= 1) {
    return;
  }

  const activeSlide = slides[activeIndex] ?? slides[0];
  const activeVideoIframe = query('[data-hero-video-iframe]', activeSlide);

  if (activeVideoIframe) {
    playActiveHeroVideo(activeVideoIframe, activeIndex, ctx, slides, state);
    scheduleVideoFallback(ctx, slides, activeIndex, activeVideoIframe, state);
    return;
  }

  state.autoplayTimer = window.setTimeout(() => {
    if (isHeroSlideActive(ctx, activeIndex)) {
      advanceHeroAutoplay(ctx, slides, activeIndex, state);
    }
  }, HERO_AUTOPLAY_IMAGE_DELAY_MS);
}

function syncHeroVideos(slides, activeIndex) {
  slides.forEach((slide, index) => {
    const iframe = query('[data-hero-video-iframe]', slide);
    if (!iframe) {
      return;
    }

    if (index === activeIndex) {
      ensureHeroVideoSrc(iframe);
      return;
    }

    stopInactiveHeroVideo(iframe);
  });
}

function updateHeroNavState(prevButton, nextButton, dots, activeIndex, slidesCount) {
  if (prevButton) {
    const isAtStart = slidesCount <= 1 ? true : false;
    prevButton.disabled = isAtStart;
    prevButton.classList.toggle('is-disabled', isAtStart);
    prevButton.setAttribute('aria-disabled', isAtStart ? 'true' : 'false');
  }

  if (nextButton) {
    const isAtEnd = slidesCount <= 1 ? true : false;
    nextButton.disabled = isAtEnd;
    nextButton.classList.toggle('is-disabled', isAtEnd);
    nextButton.setAttribute('aria-disabled', isAtEnd ? 'true' : 'false');
  }

  dots.forEach((dot, index) => {
    const isActive = index === activeIndex;
    dot.classList.toggle('is-active', isActive);
    dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
}

export function initHeroAnimation(ctx) {
  const sliderTrack = query('[data-hero-slider-track]');
  if (!sliderTrack) {
    return ctx;
  }

  const heroSection = sliderTrack.closest('[data-hero]');
  const slides = queryAll('[data-hero-slide]', sliderTrack);
  const prevButton = query('[data-hero-prev]', heroSection);
  const nextButton = query('[data-hero-next]', heroSection);
  const dots = queryAll('[data-hero-dot]', heroSection);
  const hasMultipleSlides = slides.length > 1;
  const autoplayState = {
    autoplayTimer: null,
    videoDurationTimer: null,
    videoPlayToken: 0,
  };
  ctx.heroSlides = slides;

  if (!slides.length) {
    return ctx;
  }

  const updateSlide = (slider) => {
    const activeIndex = slider?.track?.details?.rel ?? 0;
    const activeSlide = slides[activeIndex] ?? slides[0];

    setSlideState(slides, activeIndex);
    syncHeroVideos(slides, activeIndex);
    updateHeroNavState(prevButton, nextButton, dots, activeIndex, slides.length);
    resetSlideContentState(activeSlide);
    animateSlideContent(activeSlide);
    scheduleHeroAutoplay(ctx, slides, activeIndex, autoplayState);
  };

  ctx.heroSliderInstance = new KeenSlider(sliderTrack, {
    drag: false,
    loop: hasMultipleSlides,
    mode: 'snap',
    renderMode: 'precision',
    slides: {
      perView: 1,
      spacing: 0,
    },
    created(slider) {
      updateSlide(slider);
    },
    slideChanged(slider) {
      updateSlide(slider);
    },
  });

  prevButton?.addEventListener('click', () => {
    ctx.heroSliderInstance?.prev();
  });

  nextButton?.addEventListener('click', () => {
    ctx.heroSliderInstance?.next();
  });

  dots.forEach((dot) => {
    dot.addEventListener('click', () => {
      const index = Number.parseInt(dot.getAttribute('data-hero-dot-index') ?? '0', 10);
      if (Number.isFinite(index)) {
        ctx.heroSliderInstance?.moveToIdx(index, true);
      }
    });
  });

  return ctx;
}
